import { 
  alertActionSchema,
  setCookieConsentSchema,
  setIntegrationConsentSchema,
  updateConsentVersionSchema,
  settingsActionSchema
} from '@/lib/validation/store/core'
import { articleStateItemSchema } from '@/lib/validation/store/features/articles'
import { galleryStateItemSchema } from '@/lib/validation/store/features/gallery'
import { locationStateItemSchema } from '@/lib/validation/store/features/locations'
import { 
  productActionValidation, 
  productCategoryStateItemSchema 
} from '@/lib/validation/store/features/products'
import { projectStateItemSchema } from '@/lib/validation/store/features/projects'
import { serviceStateItemSchema } from '@/lib/validation/store/features/services'
import { cartItemActionSchema, recentlyViewedActionSchema } from '@/lib/validation/store/shared'
import { Middleware } from '@reduxjs/toolkit'
import { z } from 'zod'

interface TypedAction {
  type: string;
  payload?: unknown;
}

// Common schema patterns
const booleanSchema = z.boolean()
const stringSchema = z.string()
const filterParamsSchema = z.record(z.string(), z.unknown()).nullable()
const paginationSchema = z.object({
  page: z.number(),
  limit: z.number(),
  total: z.number()
})

// Map action types to their validation schemas.
const actionSchemaMap: Record<string, z.ZodSchema> = {
  // Core actions
  'alerts/add': alertActionSchema,
  'privacy/setCookieConsent': setCookieConsentSchema,
  'privacy/setMicrosoftIntegrationConsent': setIntegrationConsentSchema,
  'privacy/setGoogleIntegrationConsent': setIntegrationConsentSchema,
  'privacy/setFacebookIntegrationConsent': setIntegrationConsentSchema,
  'privacy/updateConsentVersion': updateConsentVersionSchema,
  'globalSettings/update': settingsActionSchema,

  // Product actions
  ...productActionValidation,
  'products/setFilterParams': filterParamsSchema,

  // Product Category actions
  'productCategories/setCategories': productCategoryStateItemSchema.array(),
  'productCategories/updateCategory': productCategoryStateItemSchema,
  'productCategories/setSelectedCategory': productCategoryStateItemSchema.nullable(),
  'productCategories/setFilterParams': filterParamsSchema,

  // Service actions
  'services/setServices': z.object({
    services: serviceStateItemSchema.array(),
    locationId: z.string()
  }),
  'services/updateService': serviceStateItemSchema,
  'services/setSelectedService': serviceStateItemSchema.nullable(),
  'services/setFilterParams': filterParamsSchema,

  // Article actions
  'articles/setArticles': articleStateItemSchema.array(),
  'articles/updateArticle': articleStateItemSchema,
  'articles/setSelectedArticle': articleStateItemSchema.nullable(),
  'articles/setPagination': paginationSchema,
  'articles/setFilterParams': filterParamsSchema,

  // Location actions
  'locations/setLocations': locationStateItemSchema.array(),
  'locations/updateLocation': locationStateItemSchema,
  'locations/setSelectedLocation': locationStateItemSchema.nullable(),
  'locations/setFilterParams': filterParamsSchema,

  // Project actions
  'projects/setProjects': projectStateItemSchema.array(),
  'projects/updateProject': projectStateItemSchema,
  'projects/setSelectedProject': projectStateItemSchema.nullable(),
  'projects/setFilterParams': filterParamsSchema,

  // Gallery actions
  'gallery/setGallery': galleryStateItemSchema.array(),
  'gallery/updateGalleryItem': galleryStateItemSchema,
  'gallery/deleteGalleryItem': z.string(),
  'gallery/setSelectedItem': galleryStateItemSchema.nullable(),
  'gallery/setPagination': z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number()
  }),

  // Common actions for all features
  'setLoading': booleanSchema,
  'setError': stringSchema,

  // Shared actions
  'cart/addItem': cartItemActionSchema,
  'recentlyViewed/add': recentlyViewedActionSchema,
}

const isTypedAction = (action: unknown): action is TypedAction => {
  return typeof action === 'object' && action !== null && 'type' in action && typeof (action as TypedAction).type === 'string'
}

const shouldSkipValidation = (action: TypedAction): boolean => {
  return (
    action.type === 'validation/actionError' ||
    action.type === 'persist/REHYDRATE' ||
    action.type === 'persist/PERSIST' ||
    // Skip validation for internal Redux actions
    action.type.startsWith('@@redux/') ||
    // Skip validation for loading states
    action.type.includes('/pending') ||
    action.type.includes('/loading') ||
    action.type.includes('/init') ||
    // Skip for specific actions that don't need validation
    action.type === 'products/setLoading' ||
    action.type === 'products/setError' ||
    action.type === 'products/clearFilterParams' ||
    action.type === 'products/setSelectedProduct' ||
    action.type === 'services/clearFilterParams' ||
    action.type === 'articles/clearFilterParams' ||
    action.type === 'locations/clearFilterParams' ||
    action.type === 'projects/clearFilterParams' ||
    // Skip for specific privacy actions during initialization
    action.type === 'privacy/init' ||
    action.type === 'privacy/update' ||
    action.type === 'consent/update'
  )
}

const serializeDates = (payload: unknown): unknown => {
  if (!payload || typeof payload !== 'object') return payload

  const result = { ...payload as Record<string, unknown> }

  // Recursively serialize Date objects
  for (const [key, value] of Object.entries(result)) {
    if (value instanceof Date) {
      result[key] = value.toISOString()
    } else if (typeof value === 'object' && value !== null) {
      result[key] = serializeDates(value)
    }
  }

  return result
}

// Helper function to sanitize media items in products
const sanitizeProductMedia = (product: any): any => {
  if (!product || !product.media || !Array.isArray(product.media)) {
    return product
  }

  const sanitizedMedia = product.media.map((mediaItem: any, index: number) => {
    if (!mediaItem) return mediaItem

    // Replace temporary URLs with placeholder URL if needed
    let url = mediaItem.url
    if (url && (url.startsWith('blob:') || url.startsWith('data:'))) {
      // Replace with a valid placeholder URL
      url = `https://placeholder.com/image-${index}`
    }

    // Ensure metadata exists and has required fields
    const metadata = mediaItem.metadata || {}
    const sanitizedMetadata = {
      alt: metadata.alt || '',
      ownerId: metadata.ownerId || 'system',
      placement: metadata.placement || 'PRODUCT_MEDIA',
      caption: metadata.caption || '',
      orderIndex: metadata.orderIndex || index,
      ...metadata
    }

    return {
      ...mediaItem,
      url,
      metadata: sanitizedMetadata
    }
  })

  return {
    ...product,
    media: sanitizedMedia
  }
}

// Helper function to sanitize product metadata fields
const sanitizeProductMetadata = (product: any): any => {
  if (!product) return product

  return {
    ...product,
    metaTitle: product.metaTitle || '',
    metaDescription: product.metaDescription || ''
  }
}

// Helper function to fully sanitize a product
const sanitizeProduct = (product: any): any => {
  return sanitizeProductMetadata(sanitizeProductMedia(product))
}

export const actionValidationMiddleware: Middleware =
  () =>
    (next) =>
      (action) => {
        if (!isTypedAction(action)) {
          return next(action)
        }

        if (shouldSkipValidation(action)) {
          return next(action)
        }

        const schema = actionSchemaMap[action.type]
        if (!schema) {
          return next(action)
        }

        try {
          if ('payload' in action) {
            let validationPayload = action.payload

            // Handle array-like objects for products/setProducts
            if (action.type === 'products/setProducts' &&
                action.payload &&
                typeof action.payload === 'object' &&
                'length' in action.payload) {

              // Convert to array if it's array-like
              const productsArray = Array.from(action.payload as ArrayLike<unknown>)

              // Fully sanitize products before validation
              validationPayload = productsArray.map((product: any) => sanitizeProduct(product))
            }

            // Validate the payload
            schema.parse(validationPayload)

            // Pass the transformed payload if we modified it
            return next({
              ...action,
              payload: validationPayload
            })
          }
          return next(action)
        } catch (error) {
          if (error instanceof z.ZodError) {
            console.error('Action validation error:',
              JSON.stringify({
                actionType: action.type,
                errors: error.issues.map((err) => ({
                  path: err.path.join('.'),
                  message: err.message,
                  code: err.code
                })),
                payload: action.payload
              }, null, 2)
            );

            return next({
              type: 'validation/actionError',
              payload: {
                actionType: action.type,
                errors: error.issues.map((err) => ({
                  path: err.path.join('.'),
                  message: err.message,
                  code: err.code
                })),
                originalPayload: 'payload' in action ? action.payload : undefined
              }
            })
          }
          throw error
        }
      }
